import { useNavigate } from 'react-router-dom';
import { copyJson, EntityConstants } from '../../helpers/general.helper';
import { Medico, Role, Status } from '../../data/types/medico.types';
import { useEspecialidades } from '../../data/hooks/useEspecialidades.hook';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { Spinner } from '../../components/spinner/Spinner.component';
import { SmartForm } from '../../components/formFields/Form';
import { TextField } from '../../components/formFields/TextField';
import { MultiSelectDropdown } from '../../components/formFields/multiselectDropdown/MultiselectDropdown';
import { regexOnlyNumbersUpTo8, validateDni, validateEmail, validatePhone } from '../../helpers/validations.helper';
import { FileUpload } from '../../components/fileUpload/FileUpload.component';
import { api } from '../../data/api';
import { fileToBase64 } from '../../helpers/files.helper';
import { PasswordField } from '../../components/formFields/password/Password.component';
import { CheckField } from '../../components/formFields/checkField/CheckField';
import { MedTermsAndConditions } from '../../legals/TermsAndConditions.component';
import CustomModal from '../../components/modal/CustomModal';
import { ButtonInApp } from '../../pagesApp/ButtonInApp.component';
 
interface Entity extends Medico { 
	especialidadesIds: string[],
	cvTemp: File | null,
	passconf: string,
}
const entityConstants: EntityConstants = {
	apiEndpoint: '/api/authm/register',
	entityLabel: 'Medico',
	entityLabelGender: 'M',
	navigateToParent: ''
}
 
export const RegisterMedico = () => {
	const navigate = useNavigate();
 
	const { data: dataEspecialidades, isPending, isError, error } = useEspecialidades();
	 
	const [formData, setFormData] = useState<Partial<Entity>>({
		dni: '',
		apellido: '',
		nombre: '',
		email: '',
		matricula: '',
		telefono: '',
		status: Status.PENDIENTE,
		role: Role.MEDICO,
		password: '',
		passconf: '',
		especialidades: '',
		especialidadesIds: [],
		cvName: undefined,
		cvBase64: '',
		cvTemp: null,
	})
 
	const [loading, setLoading] = useState(false);
	const [showTerminos, setShowTerminos] = useState(false);
	const [aceptadoTerminos, setAceptadoTerminos] = useState(false);
	const [errors, setErrors] = useState<Record<string, string | undefined>>({});
 
	const validateForm = () => {
		const newErrors = errors === undefined ? {} : copyJson(errors)
		if (!formData.apellido) newErrors.apellido = 'El apellido es obligatorio'
		if (!formData.nombre) newErrors.nombre = 'El nombre es obligatorio'
		if (!formData.dni?.length || !validateDni(formData.dni)) newErrors.dni = 'El DNI es obligatorio'
		if (!formData.email?.length || !validateEmail(formData.email)) newErrors.email = 'El email es obligatorio'
		if (!formData.telefono?.length || !validatePhone(formData.telefono)) newErrors.telefono = 'El telefono es obligatorio'
		if (!formData.matricula?.length || formData.matricula?.length < 5) newErrors.matricula = 'La Matrícula es obligatoria (min 5 dígitos)'
		if (!formData.password?.length || formData.password.length < 8 || formData.password.length > 12) newErrors.password = 'La contraseña debe tener entre 8 y 12 characteres'
		if (!newErrors.password && formData.password !== formData.passconf) newErrors.passconf = 'Las contraseñas deben coincidir'
 
		setErrors(newErrors)
 
		return Object.keys(newErrors).length === 0;
	};
 
	const handleSubmit = async () => {
		if (!validateForm()) return;
	 
		let cvBase64 = null;
			if (formData.cvTemp) {
			cvBase64 = await fileToBase64(formData.cvTemp);
		}
 
		// Preparar datos para enviar
		const dataToSend = {
			...formData,
			especialidades: JSON.stringify(formData?.especialidadesIds?.map(e => parseInt(e)) || []),
			cvBase64: cvBase64, // Enviar como Base64
			cvName: formData?.cvTemp?.name,
			cvTemp: undefined // No enviar el File object
		};
 
		try {
			await api.post(entityConstants.apiEndpoint, dataToSend);
			toast.success('Registro enviado a Revisión!');
			navigate('/med/registerm/pending')
		} catch (err: any) {
			console.error("### error", err);
			toast.error(err.response?.data?.message || `Error guardando`);
		}
	}
 
	if (loading) return <Spinner />
 
	return (
		<SmartForm
			title={'Solicitar Registro como Médico'}
		>
			<TextField
				label="DNI"
				value={formData.dni || ''}
				onChange={(value) => {
					if (errors.dni) setErrors({...errors, dni: undefined})
					if (value.match(regexOnlyNumbersUpTo8)) setFormData({ ...formData, dni: value })
				}}
				error={errors.dni}
			/>
			<TextField
				label="Nombre"
				value={formData.nombre || ''}
				onChange={(value) => {
					if (errors.nombre) setErrors({...errors, nombre: undefined})
					setFormData({ ...formData, nombre: value })
				}}
				error={errors.nombre}
			/>
			<TextField
				label="Apellido"
				value={formData.apellido || ''}
				onChange={(value) => {
					if (errors.apellido) setErrors({...errors, apellido: undefined})
					setFormData({ ...formData, apellido: value })
				}}
				error={errors.apellido}
			/>
			<TextField
				label="Matrícula"
				value={formData.matricula || ''}
				onChange={(value) =>{
					if (errors.matricula) setErrors({...errors, matricula: undefined})
					if (value.match(regexOnlyNumbersUpTo8)) setFormData({ ...formData, matricula: value })
				}}
				error={errors.matricula}
			/>
			<MultiSelectDropdown 
				label='Especialidades'
				options={dataEspecialidades?.map(d => ({
					id: d.id.toFixed(0),
					label: d.nombre
				})) as any || []}
				value={formData.especialidadesIds || []}
				onChange={(value) => setFormData({
					...formData, 
					especialidadesIds: value,
					especialidades: JSON.stringify(value)
				})}
				error={errors.especialidades}
			/>
			<TextField
				label="Email"
				value={formData.email || ''}
				onChange={(value) => {
					if (errors.email) setErrors({... errors, email: undefined})
					setFormData({ ...formData, email: value })}}
				error={errors.email}
			/>
			<TextField
				label="Teléfono"
				value={formData.telefono || ''}
				onChange={(value) => {{
					if (errors.telefono) setErrors({...errors, telefono: undefined})
					if (validatePhone(value)) setFormData({ ...formData, telefono: value })}
				}}
				error={errors.telefono}
			/>
			<FileUpload
				label='Curriculum'
				value={formData.cvTemp || null}
				onChange={(newFile) => {
					console.log("### NEW FILE", newFile)
					setFormData({...formData, cvTemp: newFile})
				}}
				accept='.pdf,.doc,.docx'
				maxSize={5}
				// errors={errors.documento}
			/>
			<PasswordField 
				label='Elija una Contraseña'
				value={formData.password || ''}
				onChange={(value) => {
					if (errors.password) setErrors({...errors, password: undefined})
					setFormData({...formData, password: value})
				}}
				error={errors.password}
				maxLength={20}
			/>
			<PasswordField 
				label='Repita la Contraseña'
				value={formData.passconf || ''}
				onChange={(value) => {
					if (errors.passconf) setErrors({...errors, passconf: undefined})
					setFormData({...formData, passconf: value})
				}}
				error={errors.passconf}
				maxLength={20}
			/>
			<CheckField
				label='Acepto los Términos y Condiciones' 
				checked={aceptadoTerminos}	
				onChange={(value) => setAceptadoTerminos(value)}
			/>
			<p style={{ fontSize: '0.8rem', color: '#5E7390', textDecoration: 'underline', cursor: 'pointer' }} onClick={() => setShowTerminos(true)}>Haz click aquí para Leer Terminos y Condiciones</p>

			<CustomModal 
				showModal={showTerminos}
				setShowModal={setShowTerminos}
				width='70%'
				height='auto'
			>
				<MedTermsAndConditions />
				<div style={{display: 'flex', justifyContent: 'flex-end'}}>
					<ButtonInApp
						label='Cerrar'
						type='button'
						onClick={() => setShowTerminos(false)}
					/>
				</div>
			</CustomModal>
			<div style={{display: 'flex', justifyContent: 'flex-end'}}>
				<ButtonInApp
					label='Solicitar Registro'
					type='submit'
					onClick={handleSubmit}
					disabled={!aceptadoTerminos}
				/>
			</div>
		</SmartForm>
	)
}